import React from 'react';

import type { UUID } from '@aline/common';
import type { Theme } from '@mui/material';
import { alpha, Typography, useTheme } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { CircleAlert, CircleSlash, PauseCircle, Sparkles } from 'lucide-react';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { getAgentThreadRunState } from './activity-types';
import type { ActivityAgentThread, AgentThreadRunState } from './activity-types';

const SUBTITLES: Record<AgentThreadRunState, (time: string) => string> = {
  cancelled: time => `Aline AI stopped · ${time}`,
  done: time => `Aline AI activity ${time}`,
  error: time => `Aline AI failed · ${time}`,
  notStarted: time => `Aline AI didn't start · ${time}`,
  running: time => `Aline AI running · ${time}`,
};

// `running` renders the radio-style progress dot instead of an icon.
const STATE_ICON_COMPONENTS: Record<Exclude<AgentThreadRunState, 'running'>, LucideIcon> = {
  cancelled: CircleSlash,
  done: Sparkles,
  error: CircleAlert,
  notStarted: CircleAlert,
};

/**
 * Accent color per run state, from the theme palette. A keyed Record (like
 * the icon/subtitle/tint maps) so a new run state is a compile error in every
 * visual decision instead of silently rendering with the "done" treatment.
 */
function getStateColors(theme: Theme): Record<AgentThreadRunState, string> {
  return {
    cancelled: theme.appColors.secondaryType,
    done: theme.appColors.primary,
    error: theme.palette.error.main,
    notStarted: theme.palette.warning.main,
    running: theme.appColors.primary,
  };
}

function getStateHoverColors(theme: Theme): Record<AgentThreadRunState, string> {
  return {
    cancelled: theme.appColors.secondaryType,
    done: alpha(theme.appColors.primary, 0.65),
    error: theme.palette.error.dark,
    notStarted: theme.palette.warning.dark,
    running: alpha(theme.appColors.primary, 0.65),
  };
}

/**
 * Whether the card gets an accent tint per run state; untinted states stay neutral white/divider.
 * Exhaustive like the icon/subtitle/color maps, so a new run state is a compile error in every
 * visual decision instead of silently rendering with the "done" treatment.
 */
const STATE_TINTED = {
  cancelled: false,
  done: false,
  error: true,
  notStarted: true,
  running: true,
} satisfies Record<AgentThreadRunState, boolean>;

const useStyles = makeStyles<{ runState: AgentThreadRunState }>()((theme, { runState }) => {
  const accent = getStateColors(theme)[runState];
  const hoverAccent = getStateHoverColors(theme)[runState];
  const tinted = STATE_TINTED[runState];
  return {
    action: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      gap: theme.spacing(0.5),
      padding: 0,
    },
    card: {
      '&:focus-visible, &:hover': {
        borderColor: hoverAccent,
      },
      alignItems: 'stretch',
      backgroundColor: tinted ? alpha(accent, 0.04) : theme.palette.common.white,
      border: `1px solid ${tinted ? accent : theme.palette.divider}`,
      borderRadius: 8,
      boxShadow: '0px 0px 3.4px rgba(0,0,0,0.05)',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5),
      width: '100%',
    },
    mainRow: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1.25),
      width: '100%',
    },
    meta: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      gap: 2,
      minWidth: 0,
    },
    // Radio-style filled dot for the running state (matches the design's progress indicator).
    runningDot: {
      alignItems: 'center',
      backgroundColor: alpha(theme.appColors.primary, 0.18),
      borderRadius: '50%',
      display: 'flex',
      flexShrink: 0,
      height: 18,
      justifyContent: 'center',
      width: 18,
    },
    runningDotInner: {
      backgroundColor: theme.appColors.primary,
      borderRadius: '50%',
      height: 9,
      width: 9,
    },
    stopLabel: {
      color: theme.appColors.primary,
      fontSize: 14,
      fontWeight: 600,
    },
    subtitle: {
      color: theme.appColors.iconsType,
      fontSize: 13,
      opacity: 0.6,
    },
    title: {
      color: theme.appColors.iconsType,
      fontSize: 14,
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };
});

interface ActivityAgentThreadCardProps {
  agentThread: ActivityAgentThread;
  onViewThread: (threadId: UUID) => void;
  time: string;
  /** Whether the current user owns this thread (only the owner may stop a running run). */
  canStop?: boolean;
}

export function ActivityAgentThreadCard({
  agentThread,
  canStop = false,
  onViewThread,
  time,
}: ActivityAgentThreadCardProps): JSX.Element {
  const theme = useTheme();
  const runState = getAgentThreadRunState(agentThread);
  const running = runState === 'running';
  const stateColors = getStateColors(theme);
  const { classes } = useStyles({ runState });
  const [stopChatMessageStream, { loading: stopping }] = Apollo.useStopChatMessageStreamMutation();

  /**
   * The thread title is auto-generated by the agent from the first prompt; until then it's the
   * placeholder "Chat", so fall back to a generic label.
   */
  const title = agentThread.title && agentThread.title !== 'Chat' ? agentThread.title : 'Aline AI';
  const subtitle = SUBTITLES[runState](time);

  function handleStop(event: React.MouseEvent): void {
    event.stopPropagation();
    /**
     * Observe the rejection: a network-failed stop would otherwise become an unhandled promise
     * rejection. The global Apollo errorLink already surfaces the failure to the user.
     */
    stopChatMessageStream({ variables: { threadId: agentThread.id } }).catch(() => undefined);
  }

  function handleView(): void {
    onViewThread(agentThread.id);
  }

  return (
    <div className={classes.card} onClick={handleView}>
      <div className={classes.mainRow}>
        {running ? (
          <span className={classes.runningDot}>
            <span className={classes.runningDotInner} />
          </span>
        ) : (
          <StateIcon runState={runState} stateColors={stateColors} />
        )}
        <div className={classes.meta}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </div>
        {running && canStop && (
          <button
            aria-label="Stop AI thread"
            className={classes.action}
            disabled={stopping}
            type="button"
            onClick={handleStop}
          >
            <PauseCircle color={theme.appColors.primary} size={16} />
            <span className={classes.stopLabel}>Stop</span>
          </button>
        )}
      </div>
    </div>
  );
}

function StateIcon({
  runState,
  stateColors,
}: {
  runState: Exclude<AgentThreadRunState, 'running'>;
  stateColors: Record<AgentThreadRunState, string>;
}): JSX.Element {
  const Icon = STATE_ICON_COMPONENTS[runState];
  return <Icon color={stateColors[runState]} size={18} style={{ flexShrink: 0 }} />;
}
